import React, { useMemo, useState } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * <PhoneInput /> — controlled phone-number field with a country-code picker.
 *
 * The parent works with a SINGLE E.164-style string (e.g. "+919876543210").
 * Under the hood the component maintains the dial code + local number
 * separately for a clean UX, and always emits the combined value via onChange.
 *
 *   <PhoneInput value={phone} onChange={setPhone} />
 *
 * On mount, if `value` already starts with a `+`, we split it into
 * dial-code + national number. Otherwise the default country is India (+91).
 */

// Top-20 markets (by user weight for a memory-vault app) + full ISO list below.
// Each entry: [ISO-2, dial code (with '+'), display label]
export const COUNTRIES = [
  ["IN", "+91",  "India"],
  ["US", "+1",   "United States"],
  ["GB", "+44",  "United Kingdom"],
  ["AE", "+971", "United Arab Emirates"],
  ["SG", "+65",  "Singapore"],
  ["AU", "+61",  "Australia"],
  ["CA", "+1",   "Canada"],
  ["DE", "+49",  "Germany"],
  ["FR", "+33",  "France"],
  ["NL", "+31",  "Netherlands"],
  ["SA", "+966", "Saudi Arabia"],
  ["QA", "+974", "Qatar"],
  ["OM", "+968", "Oman"],
  ["KW", "+965", "Kuwait"],
  ["BH", "+973", "Bahrain"],
  ["MY", "+60",  "Malaysia"],
  ["TH", "+66",  "Thailand"],
  ["ID", "+62",  "Indonesia"],
  ["PH", "+63",  "Philippines"],
  ["VN", "+84",  "Vietnam"],
  ["JP", "+81",  "Japan"],
  ["KR", "+82",  "South Korea"],
  ["CN", "+86",  "China"],
  ["HK", "+852", "Hong Kong"],
  ["TW", "+886", "Taiwan"],
  ["NP", "+977", "Nepal"],
  ["BD", "+880", "Bangladesh"],
  ["LK", "+94",  "Sri Lanka"],
  ["PK", "+92",  "Pakistan"],
  ["AF", "+93",  "Afghanistan"],
  ["MM", "+95",  "Myanmar"],
  ["BT", "+975", "Bhutan"],
  ["MV", "+960", "Maldives"],
  ["ZA", "+27",  "South Africa"],
  ["NG", "+234", "Nigeria"],
  ["KE", "+254", "Kenya"],
  ["EG", "+20",  "Egypt"],
  ["MA", "+212", "Morocco"],
  ["TR", "+90",  "Turkey"],
  ["IL", "+972", "Israel"],
  ["RU", "+7",   "Russia"],
  ["BR", "+55",  "Brazil"],
  ["MX", "+52",  "Mexico"],
  ["AR", "+54",  "Argentina"],
  ["CL", "+56",  "Chile"],
  ["CO", "+57",  "Colombia"],
  ["ES", "+34",  "Spain"],
  ["IT", "+39",  "Italy"],
  ["PT", "+351", "Portugal"],
  ["PL", "+48",  "Poland"],
  ["SE", "+46",  "Sweden"],
  ["NO", "+47",  "Norway"],
  ["FI", "+358", "Finland"],
  ["DK", "+45",  "Denmark"],
  ["CH", "+41",  "Switzerland"],
  ["AT", "+43",  "Austria"],
  ["BE", "+32",  "Belgium"],
  ["IE", "+353", "Ireland"],
  ["GR", "+30",  "Greece"],
  ["CZ", "+420", "Czech Republic"],
  ["NZ", "+64",  "New Zealand"],
];

// ISO alpha-2 → 🇦🇦 flag emoji (each letter offsets to a regional indicator)
function isoToFlag(iso) {
  if (!iso || iso.length !== 2) return "🌐";
  const A = 0x1F1E6;
  const codes = [...iso.toUpperCase()].map((c) => A + (c.charCodeAt(0) - 65));
  return String.fromCodePoint(...codes);
}

// Fast lookup by ISO
const BY_ISO = Object.fromEntries(COUNTRIES.map((c) => [c[0], c]));
// Longest-first dial codes for prefix matching
const DIAL_MATCH = [...new Set(COUNTRIES.map((c) => c[1]))].sort((a, b) => b.length - a.length);

/** Split "+919876543210" → ["+91", "9876543210"]. Handles "+1..." vs "+91..." greedily. */
export function splitE164(value) {
  const v = (value || "").replace(/[\s\-()]/g, "");
  if (!v.startsWith("+")) return { dial: "+91", local: v };
  for (const d of DIAL_MATCH) {
    if (v.startsWith(d)) return { dial: d, local: v.slice(d.length) };
  }
  return { dial: "+91", local: v.replace(/^\+/, "") };
}

/** Reverse: guess ISO from dial code — first country that matches (US wins over CA for +1, that's OK). */
function isoForDial(dial) {
  const hit = COUNTRIES.find((c) => c[1] === dial);
  return hit ? hit[0] : "IN";
}

export default function PhoneInput({
  value = "",
  onChange,
  placeholder = "98765 43210",
  disabled = false,
  className,
  inputTestId = "phone-input-national",
  triggerTestId = "phone-input-country",
  autoFocus = false,
}) {
  const { dial: initDial, local: initLocal } = useMemo(() => splitE164(value), [value]);
  const [dial, setDial] = useState(initDial || "+91");
  const [iso, setIso] = useState(() => isoForDial(initDial || "+91"));
  const [local, setLocal] = useState(initLocal || "");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  // Sync in when parent value changes externally
  React.useEffect(() => {
    const parts = splitE164(value);
    setDial(parts.dial);
    setIso(isoForDial(parts.dial));
    setLocal(parts.local);
  }, [value]);

  const emit = (newDial, newLocal) => {
    const clean = (newLocal || "").replace(/[^\d]/g, "");
    onChange?.(clean ? `${newDial}${clean}` : "");
  };

  const filteredCountries = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(([code, dial, name]) =>
      name.toLowerCase().includes(q) || dial.includes(q) || code.toLowerCase().includes(q)
    );
  }, [query]);

  return (
    <div className={cn("flex items-stretch gap-2", className)}>
      <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQuery(""); }}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            data-testid={triggerTestId}
            className={cn(
              "h-10 shrink-0 px-3 rounded-md border border-input bg-background",
              "flex items-center gap-1.5 text-sm font-medium tabular-nums",
              "hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
              disabled && "opacity-60 cursor-not-allowed",
            )}
            aria-label="Country code"
          >
            <span className="text-base leading-none" aria-hidden>{isoToFlag(iso)}</span>
            <span>{dial}</span>
            <ChevronDown className="h-3.5 w-3.5 opacity-60" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-0" data-testid="phone-input-country-list">
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search country or +code"
                className="pl-8 h-9 text-sm"
                data-testid="phone-input-country-search"
              />
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {filteredCountries.length === 0 && (
              <div className="text-xs text-muted-foreground py-6 text-center">No match</div>
            )}
            {filteredCountries.map(([code, d, name]) => {
              const selected = iso === code && dial === d;
              return (
                <button
                  key={`${code}-${d}`}
                  type="button"
                  onClick={() => {
                    setIso(code);
                    setDial(d);
                    setOpen(false);
                    emit(d, local);
                  }}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted text-left",
                    selected && "bg-primary/5 text-primary font-medium"
                  )}
                  data-testid={`phone-input-country-item-${code}`}
                >
                  <span className="text-base leading-none" aria-hidden>{isoToFlag(code)}</span>
                  <span className="flex-1 truncate">{name}</span>
                  <span className="font-mono text-muted-foreground text-xs">{d}</span>
                  {selected && <Check className="h-3.5 w-3.5 text-primary" />}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
      <Input
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        autoFocus={autoFocus}
        value={local}
        onChange={(e) => {
          const cleaned = e.target.value.replace(/[^\d]/g, "").slice(0, 15);
          setLocal(cleaned);
          emit(dial, cleaned);
        }}
        placeholder={placeholder}
        disabled={disabled}
        className="flex-1 tabular-nums"
        data-testid={inputTestId}
      />
    </div>
  );
}
